"use client";

import * as React from "react";
import {
  IconDotsVertical,
  IconTrash,
  IconPlus,
  IconMail,
} from "@tabler/icons-react";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { forgotPassword } from "@/services/auth";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AddAdminDialog } from "@/features/admin/components";
import { useDeleteAdmin } from "@/hooks/use-admins";
import { useAuth } from "@/contexts/auth-context";

// Admin schema
export const adminSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  status: z.enum(["active", "pending", "inactive", "invited"]).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  isDefault: z.boolean().optional(),
});

export type Admin = z.infer<typeof adminSchema>;

interface AdminsTableProps {
  data: Admin[];
  isLoading?: boolean;
  error?: string | null;
  totalCount?: number;
  pageIndex: number;
  pageSize: number;
  searchQuery: string;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearchChange: (search: string) => void;
}

export function AdminsTable({
  data,
  isLoading = false,
  error = null,
  totalCount = 0,
  pageIndex,
  pageSize,
  searchQuery,
  onPageChange,
  onPageSizeChange: _onPageSizeChange,
  onSearchChange,
}: AdminsTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [adminToRemove, setAdminToRemove] = React.useState<Admin | null>(null);

  const auth = useAuth();

  // Delete mutation
  const deleteMutation = useDeleteAdmin();

  // Send reset password link mutation
  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => forgotPassword(email),
  });

  const handleRemoveClick = (admin: Admin) => {
    setAdminToRemove(admin);
  };

  const handleSendResetLink = (admin: Admin) => {
    resetPasswordMutation.mutate(admin.email, {
      onSuccess: (response) => {
        toast.success(response.message);
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to send reset password link";
        toast.error(errorMessage);
      },
    });
  };

  const handleConfirmRemove = () => {
    if (adminToRemove) {
      deleteMutation.mutate(adminToRemove.id, {
        onSuccess: () => {
          toast.success(`Admin "${adminToRemove.email}" removed successfully!`);
          setAdminToRemove(null);
        },
        onError: (error) => {
          const errorMessage =
            typeof error === "string"
              ? error
              : error?.message || "Failed to remove admin";
          toast.error(errorMessage);
          setAdminToRemove(null);
        },
      });
    }
  };

  // Column definitions
  const columns: ColumnDef<Admin>[] = React.useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        cell: ({ row }) => {
          const firstName = row.original.firstName;
          const lastName = row.original.lastName;
          const name = [firstName, lastName].filter(Boolean).join(" ") || "-";
          return <div className="font-medium">{name}</div>;
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <div className="font-medium">{row.getValue("email")}</div>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const status = row.getValue("status") as
            | "active"
            | "pending"
            | "inactive"
            | "invited"
            | undefined;
          if (!status) {
            return <div className="text-muted-foreground">-</div>;
          }
          const variants = {
            active: "default",
            pending: "secondary",
            inactive: "outline",
            invited: "secondary",
          } as const;
          return (
            <Badge variant={variants[status]} className="capitalize">
              {status}
            </Badge>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        cell: ({ row }) => {
          const date = new Date(row.getValue("createdAt"));
          return (
            <div className="text-muted-foreground">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const admin = row.original;
          const isDefaultAdmin = admin.isDefault === true;
          const isCurrentUser = auth?.member?.email_address === admin.email;

          if (isDefaultAdmin || isCurrentUser) {
            return null;
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="data-[state=open]:bg-muted size-8"
                  size="icon"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconDotsVertical className="size-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {admin.status === "active" && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendResetLink(admin);
                    }}
                    disabled={resetPasswordMutation.isPending}
                  >
                    <IconMail className="mr-2 size-4" />
                    {resetPasswordMutation.isPending
                      ? "Sending..."
                      : "Send reset password link"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveClick(admin);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <IconTrash className="mr-2 size-4" />
                  {deleteMutation.isPending ? "Removing..." : "Remove"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [
      auth?.member?.email_address,
      deleteMutation.isPending,
      resetPasswordMutation.isPending,
    ]
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(totalCount / pageSize),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
  });

  const totalPages = Math.ceil(totalCount / pageSize);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < totalPages - 1;

  return (
    <div className="w-full space-y-4">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between">
        <Input
          placeholder="Search admins..."
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          className="max-w-sm"
          disabled={isLoading}
        />
        <AddAdminDialog>
          <Button>
            <IconPlus className="mr-2 size-4" />
            Add Admin
          </Button>
        </AddAdminDialog>
      </div>

      {/* Remove Admin Confirmation Dialog */}
      <AlertDialog
        open={!!adminToRemove}
        onOpenChange={() => setAdminToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Admin</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{adminToRemove?.email}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Error loading admins: {error}
          </p>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader className="bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="ml-2 text-muted-foreground">
                      Loading admins...
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    {searchQuery
                      ? "No admins found matching your search."
                      : "No admins found. Add one to get started."}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="text-muted-foreground flex-1 text-sm">
          Showing {data.length > 0 ? pageIndex * pageSize + 1 : 0} to{" "}
          {Math.min((pageIndex + 1) * pageSize, totalCount)} of {totalCount}{" "}
          admins
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={!canPreviousPage || isLoading}
          >
            Previous
          </Button>
          <div className="text-sm font-medium">
            Page {totalPages > 0 ? pageIndex + 1 : 0} of {totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={!canNextPage || isLoading}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
